import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, Card, CardContent, CardActions,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Tabs, Tab, Divider, Alert, CircularProgress,
  Collapse, Autocomplete, Tooltip, LinearProgress, FormControlLabel, Switch,
} from '@mui/material';
import {
  Add, Edit, Delete, ExpandMore, ExpandLess, Restaurant,
  Lock, LockOpen, Close, Search, CalendarMonth,
  LockOutlined, LockOpenOutlined, Upload, Download, TableChart, AssignmentTurnedIn,
  AutoAwesome,
} from '@mui/icons-material';
import { mealPlansAPI, weeklySchedulesAPI } from '../services/api';
import { useAppData } from '../context/AppDataContext';
import AssignPlanDialog from '../components/AssignPlanDialog';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';

const MEAL_TYPES = [
  { value: 'breakfast',      label: 'Breakfast',        time: '07:00' },
  { value: 'morningSnack',   label: 'Morning Snack',    time: '10:00' },
  { value: 'preWorkout',     label: 'Pre-Workout',      time: '06:30' },
  { value: 'lunch',          label: 'Lunch',            time: '13:00' },
  { value: 'afternoonSnack', label: 'Afternoon Snack',  time: '16:00' },
  { value: 'postWorkout',    label: 'Post-Workout',     time: '08:30' },
  { value: 'dinner',         label: 'Dinner',           time: '19:00' },
  { value: 'eveningSnack',   label: 'Evening Snack',    time: '21:00' },
];

const DAY_NAMES = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

const QUANTITY_UNITS = [
  { value: 'grams',      label: 'Grams (g)',             approxG: null },
  { value: 'small_bowl', label: 'Small Bowl (~200g)',     approxG: 200  },
  { value: 'large_bowl', label: 'Large Bowl (~400g)',     approxG: 400  },
  { value: 'count',      label: 'Count (pieces/numbers)', approxG: null },
];

// eslint-disable-next-line no-unused-vars
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2);

function getCurrentUser() {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
}

// eslint-disable-next-line no-unused-vars
const emptyPlan = () => {
  // Food items matching the Excel template "Food Options" sheet — grouped by meal type
  // These are the same items used in the template so imported plans look consistent
  // One item per category is marked as recommended (isRecommended: true)
  const SHEET_FOODS = {
    breakfast: [
      { name: 'Vegetable Oats',        weightG: 50,  calories: 350, proteinG: 10, carbsG: 45, fatG: 10, fiberG: 6, isRecommended: true },
      { name: 'Besan Chilla + Paneer', weightG: 110, calories: 400, proteinG: 22, carbsG: 35, fatG: 15, fiberG: 6, isRecommended: false },
      { name: 'Upma',                  weightG: 60,  calories: 330, proteinG:  8, carbsG: 45, fatG: 10, fiberG: 4, isRecommended: false },
      { name: 'Poha',                  weightG: 60,  calories: 320, proteinG:  7, carbsG: 50, fatG: 10, fiberG: 4, isRecommended: false },
      { name: 'Smoothie',              weightG: 200, calories: 300, proteinG: 12, carbsG: 35, fatG: 12, fiberG: 4, isRecommended: false },
    ],
    lunch: [
      { name: 'Dal Roti Sabzi Curd',   weightG: 100, calories: 550, proteinG: 22, carbsG: 75, fatG: 15, fiberG: 10, isRecommended: true },
      { name: 'Rajma Rice',            weightG: 150, calories: 520, proteinG: 18, carbsG: 75, fatG:  8, fiberG: 12, isRecommended: false },
      { name: 'Chole Roti',            weightG: 150, calories: 540, proteinG: 20, carbsG: 70, fatG: 12, fiberG: 12, isRecommended: false },
      { name: 'Soya Chunk Curry',      weightG: 30,  calories: 500, proteinG: 25, carbsG: 40, fatG: 10, fiberG:  8, isRecommended: false },
      { name: 'Paneer Bhurji Roti',    weightG: 100, calories: 550, proteinG: 22, carbsG: 35, fatG: 25, fiberG:  5, isRecommended: false },
    ],
    afternoonSnack: [
      { name: 'Roasted Chana',  weightG: 30,  calories: 120, proteinG: 6, carbsG: 18, fatG:  2, fiberG: 5, isRecommended: true },
      { name: 'Peanuts',        weightG: 25,  calories: 150, proteinG: 6, carbsG:  6, fatG: 12, fiberG: 3, isRecommended: false },
      { name: 'Sprouts Salad',  weightG: 100, calories: 100, proteinG: 7, carbsG: 15, fatG:  1, fiberG: 5, isRecommended: false },
      { name: 'Curd + Seeds',   weightG: 150, calories: 140, proteinG: 8, carbsG: 10, fatG:  6, fiberG: 2, isRecommended: false },
      { name: 'Fruit + Nuts',   weightG: 100, calories: 140, proteinG: 3, carbsG: 18, fatG:  6, fiberG: 4, isRecommended: false },
    ],
    dinner: [
      { name: 'Paneer Veggies', weightG: 100, calories: 400, proteinG: 20, carbsG: 10, fatG: 25, fiberG: 4, isRecommended: true },
      { name: 'Tofu Stir Fry',  weightG: 120, calories: 350, proteinG: 20, carbsG: 10, fatG: 10, fiberG: 4, isRecommended: false },
      { name: 'Dal Soup Roti',  weightG: 150, calories: 300, proteinG: 12, carbsG: 30, fatG:  5, fiberG: 6, isRecommended: false },
      { name: 'Soya Salad',     weightG: 30,  calories: 250, proteinG: 25, carbsG: 15, fatG:  2, fiberG: 6, isRecommended: false },
      { name: 'Veg Khichdi',    weightG: 60,  calories: 300, proteinG: 10, carbsG: 45, fatG:  8, fiberG: 5, isRecommended: false },
    ],
  };

  const mealDef = [
    { mealType: 'breakfast',      mealName: 'Breakfast', time: '07:00', foods: SHEET_FOODS.breakfast },
    { mealType: 'afternoonSnack', mealName: 'Snack',     time: '16:00', foods: SHEET_FOODS.afternoonSnack },
    { mealType: 'lunch',          mealName: 'Lunch',     time: '13:00', foods: SHEET_FOODS.lunch },
    { mealType: 'dinner',         mealName: 'Dinner',    time: '19:00', foods: SHEET_FOODS.dinner },
  ];

  // Calculate daily totals from recommended items
  const recommendedItems = [
    ...SHEET_FOODS.breakfast.filter(f => f.isRecommended),
    ...SHEET_FOODS.lunch.filter(f => f.isRecommended),
    ...SHEET_FOODS.afternoonSnack.filter(f => f.isRecommended),
    ...SHEET_FOODS.dinner.filter(f => f.isRecommended),
  ];
  
  const dailyCaloriesTarget = recommendedItems.reduce((sum, item) => sum + item.calories, 0);
  const dailyProteinTarget = recommendedItems.reduce((sum, item) => sum + item.proteinG, 0);
  const dailyCarbsTarget = recommendedItems.reduce((sum, item) => sum + item.carbsG, 0);
  const dailyFatsTarget = recommendedItems.reduce((sum, item) => sum + item.fatG, 0);

  // Generate 30 days — each day has ALL food items from each category
  const days = [];
  for (let i = 1; i <= 30; i++) {
    const meals = mealDef.map(({ mealType, mealName, time, foods }) => {
      // Include ALL foods from this category as items
      const items = foods.map(food => ({ 
        ...food, 
        foodItemId: null,
        isRecommended: food.isRecommended || false,
      }));
      
      // Calculate totals from recommended items only
      const recommendedInMeal = items.filter(item => item.isRecommended);
      const totalCalories = recommendedInMeal.reduce((sum, item) => sum + item.calories, 0);
      const totalProteinG = recommendedInMeal.reduce((sum, item) => sum + item.proteinG, 0);
      const totalCarbsG   = recommendedInMeal.reduce((sum, item) => sum + item.carbsG, 0);
      const totalFatG     = recommendedInMeal.reduce((sum, item) => sum + item.fatG, 0);
      
      return {
        mealType,
        mealName,
        time,
        items,
        totalCalories,
        totalProteinG,
        totalCarbsG,
        totalFatG,
      };
    });
    days.push({ dayNumber: i, dayName: `Day ${i}`, meals });
  }

  return {
    name: '',
    description: 'Strict meal plan - Users can only log food items from the prescribed list. Each meal type has multiple options to choose from.',
    goal: 'healthy_eating',
    dietType: 'regular',
    planMode: 'strict', // Users can only log items from this plan
    durationWeeks: 4,
    isFree: true,
    isPublished: false,
    dailyCaloriesTarget,
    dailyProteinTarget,
    dailyCarbsTarget,
    dailyFatsTarget,
    tags: ['strict', 'prescribed'],
    days,
  };
};

const blankMealPlan = () => ({
  name: '',
  description: '',
  goal: 'healthy_eating',
  dietType: 'regular',
  planMode: 'normal',
  durationWeeks: 4,
  planDays: 7,
  isFree: true,
  isPublished: false,
  dailyCaloriesTarget: 0,
  dailyProteinTarget: 0,
  dailyCarbsTarget: 0,
  dailyFatsTarget: 0,
  tags: [],
  days: [],
});

// ── Effective grams helper ─────────────────────────────────────────────────────
function effectiveGrams(unit, value, perUnitG) {
  if (unit === 'grams') return value;
  if (unit === 'small_bowl') return value * 200;
  if (unit === 'large_bowl') return value * 400;
  if (unit === 'count') return value * (perUnitG || 100); // perUnitG = grams per 1 piece
  return value;
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function MealPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [scheduleDialogPlan, setScheduleDialogPlan] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [assignPlan, setAssignPlan] = useState(null);
  const [page, setPage]   = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await mealPlansAPI.getAll({ page, limit });
      setPlans(res.data.data || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load plans');
    } finally { setLoading(false); }
  }, [page, limit]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleSave = async (plan) => {
    try {
      if (plan._id) {
        await mealPlansAPI.update(plan._id, plan);
        setSuccess('Plan updated');
      } else {
        await mealPlansAPI.create(plan);
        setSuccess('Plan created');
      }
      setBuilderOpen(false);
      fetchPlans();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save plan');
    }
  };

  const handleDelete = async () => {
    try {
      await mealPlansAPI.delete(deleteId);
      setSuccess('Plan deleted');
      setDeleteId(null);
      fetchPlans();
    } catch (e) { setError('Failed to delete'); }
  };

  const togglePublish = async (plan) => {
    try {
      await mealPlansAPI.publish(plan._id, !plan.isPublished);
      fetchPlans();
    } catch (e) { setError('Failed to update'); }
  };

  const openEditPlan = async (plan) => {
    try {
      const res = await mealPlansAPI.getOne(plan._id);
      const fullPlan = res.data.data || plan;
      const normalized = {
        ...blankMealPlan(),
        ...fullPlan,
        planMode: fullPlan.planMode || 'normal',
        days: (fullPlan.days || []).map(d => ({
          ...d,
          meals: (d.meals || []).map(m => ({ ...m, items: m.items || [] })),
        })),
        tags: fullPlan.tags || [],
      };
      setEditingPlan(normalized);
      setBuilderOpen(true);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load meal plan details');
    }
  };

  return (
    <Box>
      <PageHeader
        title="Meal Plans"
        subtitle="Create and manage nutrition plans for users."
        gradient="linear-gradient(90deg, #059669, #34D399)"
        actions={<>
          <Button variant="outlined" size="small" startIcon={<TableChart />} onClick={() => setImportOpen(true)}>Import Excel</Button>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => { setEditingPlan(blankMealPlan()); setBuilderOpen(true); }}>Create Plan</Button>
        </>}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : plans.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Restaurant sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="body1" fontWeight={600} gutterBottom>No meal plans yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Create your first plan to get started.</Typography>
          <Button variant="contained" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => { setEditingPlan(blankMealPlan()); setBuilderOpen(true); }}>
            Create First Plan
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {plans.map(plan => (
            <Grid item xs={12} md={6} lg={4} key={plan._id}>
              <Card elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: 1, borderColor: 'divider', borderRadius: 2, transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' } }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.9375rem' }}>{plan.name}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Chip
                        label={plan.planMode === 'strict' ? 'Strict' : 'Normal'}
                        color={plan.planMode === 'strict' ? 'error' : 'default'}
                        size="small"
                        icon={plan.planMode === 'strict'
                          ? <LockOutlined sx={{ fontSize: '14px !important' }} />
                          : <LockOpenOutlined sx={{ fontSize: '14px !important' }} />}
                      />
                      <Chip label={plan.isPublished ? 'Published' : 'Draft'} color={plan.isPublished ? 'success' : 'default'} size="small" />
                    </Stack>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ minHeight: 40, fontSize: '0.8125rem' }}>
                    {plan.description || 'No description'}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                    <Chip label={plan.goal?.replace('_', ' ')} size="small" variant="outlined" />
                    <Chip label={plan.dietType} size="small" variant="outlined" />
                    <Chip label={`${plan.durationWeeks}w`} size="small" variant="outlined" />
                    <Chip label={plan.isFree ? 'Free' : 'Premium'} size="small" color={plan.isFree ? 'success' : 'warning'} icon={plan.isFree ? <LockOpenOutlined sx={{ fontSize: '14px !important' }} /> : <LockOutlined sx={{ fontSize: '14px !important' }} />} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    {plan.dailyCaloriesTarget} kcal/day · {plan.enrolledCount || 0} enrolled
                  </Typography>
                  {plan.planMode === 'strict' && (
                    <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: '0.75rem' }}>
                      Strict — users can only log prescribed food items
                    </Alert>
                  )}
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 0.5, borderTop: 1, borderColor: 'divider' }}>
                  <Button size="small" startIcon={<Edit />} onClick={() => openEditPlan(plan)}>Edit</Button>
                  <Button size="small" startIcon={plan.isPublished ? <LockOpen /> : <Lock />} onClick={() => togglePublish(plan)}>
                    {plan.isPublished ? 'Unpublish' : 'Publish'}
                  </Button>
                  {plan.planMode === 'strict' && (
                    <Tooltip title="Manage weekly food schedules">
                      <Button size="small" startIcon={<CalendarMonth />} color="secondary" onClick={() => setScheduleDialogPlan(plan)}>
                        Schedule
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    size="small"
                    startIcon={<AssignmentTurnedIn />}
                    onClick={() => setAssignPlan(plan)}
                    sx={{ color: '#FF6B35' }}
                  >
                    Assign
                  </Button>
                  <IconButton size="small" color="error" sx={{ ml: 'auto' }} onClick={() => setDeleteId(plan._id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <PaginationBar page={page} pages={pages} total={total} limit={limit} onPage={setPage} onLimit={v => { setLimit(v); setPage(1); }} />

      {importOpen && (
        <ImportExcelDialog
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); fetchPlans(); setSuccess('Meal plan imported successfully!'); }}
        />
      )}

      {builderOpen && editingPlan && (
        <MealPlanBuilderDialog plan={editingPlan} onSave={handleSave} onClose={() => setBuilderOpen(false)} />
      )}

      {scheduleDialogPlan && (
        <WeeklyScheduleDialog plan={scheduleDialogPlan} onClose={() => setScheduleDialogPlan(null)} />
      )}

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Meal Plan?</DialogTitle>
        <DialogContent><Typography>This will permanently delete the plan.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Plan Dialog */}
      <AssignPlanDialog
        open={!!assignPlan}
        onClose={() => { setAssignPlan(null); setSuccess('Plan assigned successfully'); }}
        planType="meal"
        preselectedPlan={assignPlan}
      />
    </Box>
  );
}

// ── Meal Plan Builder Dialog ───────────────────────────────────────────────────
function MealPlanBuilderDialog({ plan: initialPlan, onSave, onClose }) {
  const { foodItems: cachedFoodItems, loadFoodItems } = useAppData();
  const user = getCurrentUser();
  const canUseAI = user?.role === 'superadmin';

  const [plan, setPlan] = useState(() => {
    const empty = blankMealPlan();
    return {
      ...empty,
      ...initialPlan,
      planMode: initialPlan.planMode || 'normal',
      days: (initialPlan.days && initialPlan.days.length > 0)
        ? initialPlan.days.map(d => ({
            ...d,
            meals: (d.meals || []).map(m => ({ ...m, items: m.items || [] })),
          }))
        : empty.days,
      planDays: initialPlan.planDays || initialPlan.days?.length || empty.planDays,
      tags: initialPlan.tags || [],
    };
  });
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [error, setError] = useState('');

  // Trigger lazy-load of food items (no-op if already cached)
  useEffect(() => { loadFoodItems(); }, [loadFoodItems]);

  // Use cached food items from context
  const foodItems = cachedFoodItems;

  const update = (field, value) => setPlan(p => ({ ...p, [field]: value }));

  const handleGenerateAI = async () => {
    if (!canUseAI) {
      setError('Only super admin can use AI meal generation.');
      return;
    }

    if (!plan.name.trim()) {
      setError('Plan name is required before AI generation');
      return;
    }

    const daysCount = Math.max(1, Math.min(30, Number(plan.planDays || plan.days.length || 7)));
    setAiGenerating(true);
    setError('');

    try {
      const res = await mealPlansAPI.generateAI({
        name: plan.name.trim(),
        description: plan.description,
        goal: plan.goal,
        dietType: plan.dietType,
        planMode: plan.planMode,
        durationWeeks: plan.durationWeeks,
        daysCount,
        dailyCaloriesTarget: Number(plan.dailyCaloriesTarget) || undefined,
        dailyProteinTarget: Number(plan.dailyProteinTarget) || undefined,
        dailyCarbsTarget: Number(plan.dailyCarbsTarget) || undefined,
        dailyFatsTarget: Number(plan.dailyFatsTarget) || undefined,
      });

      const generated = res.data?.data || {};
      setPlan(p => ({
        ...p,
        name: generated.name || p.name,
        description: generated.description || p.description,
        dailyCaloriesTarget: generated.dailyCaloriesTarget ?? p.dailyCaloriesTarget,
        dailyProteinTarget: generated.dailyProteinTarget ?? p.dailyProteinTarget,
        dailyCarbsTarget: generated.dailyCarbsTarget ?? p.dailyCarbsTarget,
        dailyFatsTarget: generated.dailyFatsTarget ?? p.dailyFatsTarget,
        tags: generated.tags?.length ? generated.tags : p.tags,
        days: generated.days || p.days,
        planDays: daysCount,
      }));
      setTab(2);
    } catch (e) {
      setError(e.response?.data?.message || 'AI could not generate the meal plan. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  const addDay = () => {
    const n = plan.days.length;
    setPlan(p => ({ ...p, days: [...p.days, { dayNumber: n + 1, dayName: `Day ${n + 1}`, meals: [] }] }));
  };

  const removeDay = (idx) => {
    const days = plan.days.filter((_, i) => i !== idx).map((d, i) => ({ ...d, dayNumber: i + 1 }));
    setPlan(p => ({ ...p, days }));
  };

  const updateDay = (idx, field, value) => {
    const days = [...plan.days];
    days[idx] = { ...days[idx], [field]: value };
    setPlan(p => ({ ...p, days }));
  };

  const addMeal = (dayIdx) => {
    const days = [...plan.days];
    days[dayIdx].meals = [...(days[dayIdx].meals || []), {
      mealType: 'lunch', mealName: 'Lunch', time: '13:00', items: [],
      totalCalories: 0, totalProteinG: 0, totalCarbsG: 0, totalFatG: 0,
    }];
    setPlan(p => ({ ...p, days }));
  };

  const updateMeal = (dayIdx, mealIdx, field, value) => {
    const days = [...plan.days];
    days[dayIdx].meals[mealIdx] = { ...days[dayIdx].meals[mealIdx], [field]: value };
    if (field === 'mealType') {
      const mt = MEAL_TYPES.find(m => m.value === value);
      if (mt) {
        days[dayIdx].meals[mealIdx].mealName = mt.label;
        days[dayIdx].meals[mealIdx].time = mt.time;
      }
    }
    setPlan(p => ({ ...p, days }));
  };

  const removeMeal = (dayIdx, mealIdx) => {
    const days = [...plan.days];
    days[dayIdx].meals = days[dayIdx].meals.filter((_, i) => i !== mealIdx);
    setPlan(p => ({ ...p, days }));
  };

  const addFoodToMeal = (dayIdx, mealIdx, food, weightG) => {
    const w = parseFloat(weightG) || food.defaultServing || 100;
    const factor = w / 100;
    const item = {
      foodItemId: food._id || null,
      name: food.name,
      weightG: w,
      calories: +(food.cal100 * factor).toFixed(1),
      proteinG: +(food.protein100 * factor).toFixed(1),
      carbsG:   +(food.carbs100 * factor).toFixed(1),
      fatG:     +(food.fat100 * factor).toFixed(1),
      fiberG:   +((food.fiber100 || 0) * factor).toFixed(1),
    };
    const days = [...plan.days];
    const meal = { ...days[dayIdx].meals[mealIdx] };
    meal.items = [...(meal.items || []), item];
    meal.totalCalories = meal.items.reduce((s, i) => s + i.calories, 0);
    meal.totalProteinG = meal.items.reduce((s, i) => s + i.proteinG, 0);
    meal.totalCarbsG   = meal.items.reduce((s, i) => s + i.carbsG, 0);
    meal.totalFatG     = meal.items.reduce((s, i) => s + i.fatG, 0);
    days[dayIdx].meals[mealIdx] = meal;
    setPlan(p => ({ ...p, days }));
  };

  const removeFoodFromMeal = (dayIdx, mealIdx, itemIdx) => {
    const days = [...plan.days];
    const meal = { ...days[dayIdx].meals[mealIdx] };
    meal.items = meal.items.filter((_, i) => i !== itemIdx);
    meal.totalCalories = meal.items.reduce((s, i) => s + i.calories, 0);
    meal.totalProteinG = meal.items.reduce((s, i) => s + i.proteinG, 0);
    meal.totalCarbsG   = meal.items.reduce((s, i) => s + i.carbsG, 0);
    meal.totalFatG     = meal.items.reduce((s, i) => s + i.fatG, 0);
    days[dayIdx].meals[mealIdx] = meal;
    setPlan(p => ({ ...p, days }));
  };

  const handleSave = async () => {
    if (!plan.name.trim()) { setError('Plan name is required'); return; }
    setSaving(true);
    try {
      const { planDays, ...payload } = plan;
      await onSave(payload);
    }
    catch (e) { setError(e.message || 'Save failed'); setSaving(false); }
  };

  const handleNext = () => {
    if (tab === 0 && !plan.name.trim()) {
      setError('Plan name is required');
      return;
    }
    setError('');
    setTab(t => Math.min(t + 1, 2));
  };

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{plan._id ? 'Edit' : 'Create'} Meal Plan</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Basic Info" />
          <Tab label="Nutrition Targets" />
          <Tab label={`Days & Meals (${plan.days.length})`} />
        </Tabs>

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {/* Tab 0: Basic Info */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField label="Plan Name *" value={plan.name} onChange={e => update('name', e.target.value)} fullWidth /></Grid>
              <Grid item xs={12}><TextField label="Description" value={plan.description} onChange={e => update('description', e.target.value)} fullWidth multiline rows={3} /></Grid>
              <Grid item xs={6} sm={3}>
                <TextField select label="Goal" value={plan.goal} onChange={e => update('goal', e.target.value)} fullWidth>
                  {['weight_loss','muscle_gain','maintenance','healthy_eating'].map(g => <MenuItem key={g} value={g}>{g.replace('_',' ')}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField select label="Diet Type" value={plan.dietType} onChange={e => update('dietType', e.target.value)} fullWidth>
                  {['regular','vegetarian','vegan','keto','paleo','mediterranean'].map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField label="Duration (weeks)" type="number" value={plan.durationWeeks} onChange={e => update('durationWeeks', +e.target.value)} fullWidth inputProps={{ min: 1 }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Plan Days"
                  type="number"
                  value={plan.planDays || ''}
                  onChange={e => update('planDays', Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                  fullWidth
                  helperText="Number of plan days"
                  inputProps={{ min: 1, max: 30 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Access Type</Typography>
                <Stack direction="row" spacing={2}>
                  <Card
                    variant="outlined"
                    onClick={() => update('isFree', true)}
                    sx={{
                      p: 2, cursor: 'pointer', flex: 1,
                      borderColor: plan.isFree ? 'success.main' : 'divider',
                      borderWidth: plan.isFree ? 2 : 1,
                      bgcolor: plan.isFree ? 'success.50' : 'background.paper',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                      <LockOpenOutlined color={plan.isFree ? 'success' : 'disabled'} />
                      <Typography fontWeight={600}>Free</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Available to all users. No subscription required.</Typography>
                  </Card>
                  <Card
                    variant="outlined"
                    onClick={() => update('isFree', false)}
                    sx={{
                      p: 2, cursor: 'pointer', flex: 1,
                      borderColor: !plan.isFree ? 'warning.main' : 'divider',
                      borderWidth: !plan.isFree ? 2 : 1,
                      bgcolor: !plan.isFree ? 'warning.50' : 'background.paper',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                      <LockOutlined color={!plan.isFree ? 'warning' : 'disabled'} />
                      <Typography fontWeight={600}>Premium Only</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Only accessible to users with an active Premium subscription.</Typography>
                  </Card>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Plan Mode</Typography>
                <Stack direction="row" spacing={2}>
                  <Card variant="outlined" onClick={() => update('planMode', 'normal')} sx={{ p: 2, cursor: 'pointer', flex: 1, borderColor: plan.planMode === 'normal' ? 'primary.main' : 'divider', borderWidth: plan.planMode === 'normal' ? 2 : 1, bgcolor: plan.planMode === 'normal' ? 'primary.50' : 'background.paper' }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                      <LockOpenOutlined color={plan.planMode === 'normal' ? 'primary' : 'disabled'} />
                      <Typography fontWeight={600}>Normal</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Users can log any food freely. Meal plan serves as a guide/template.</Typography>
                  </Card>
                  <Card variant="outlined" onClick={() => update('planMode', 'strict')} sx={{ p: 2, cursor: 'pointer', flex: 1, borderColor: plan.planMode === 'strict' ? 'error.main' : 'divider', borderWidth: plan.planMode === 'strict' ? 2 : 1, bgcolor: plan.planMode === 'strict' ? 'error.50' : 'background.paper' }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                      <LockOutlined color={plan.planMode === 'strict' ? 'error' : 'disabled'} />
                      <Typography fontWeight={600}>Strict</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Users can ONLY log the prescribed food items. Trainer creates weekly schedules.</Typography>
                  </Card>
                </Stack>
                {plan.planMode === 'strict' && (
                  <Alert severity="warning" sx={{ mt: 1 }}>After saving, use the "Schedule" button on the plan card to create weekly food schedules.</Alert>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField label="Tags (comma separated)" value={(plan.tags || []).join(', ')} onChange={e => update('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))} fullWidth />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={plan.isPublished} onChange={e => update('isPublished', e.target.checked)} />} label="Published (visible to users)" />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 1: Nutrition Targets */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" mb={2}>Daily nutritional targets for this plan</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}><TextField label="Calories (kcal)" type="number" value={plan.dailyCaloriesTarget} onChange={e => update('dailyCaloriesTarget', +e.target.value)} fullWidth /></Grid>
              <Grid item xs={6} sm={3}><TextField label="Protein (g)" type="number" value={plan.dailyProteinTarget} onChange={e => update('dailyProteinTarget', +e.target.value)} fullWidth /></Grid>
              <Grid item xs={6} sm={3}><TextField label="Carbs (g)" type="number" value={plan.dailyCarbsTarget} onChange={e => update('dailyCarbsTarget', +e.target.value)} fullWidth /></Grid>
              <Grid item xs={6} sm={3}><TextField label="Fats (g)" type="number" value={plan.dailyFatsTarget} onChange={e => update('dailyFatsTarget', +e.target.value)} fullWidth /></Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 2: Days & Meals */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>Meal Days</Typography>
              <Stack direction="row" spacing={1}>
                {canUseAI && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={aiGenerating ? <CircularProgress size={16} /> : <AutoAwesome />}
                    onClick={handleGenerateAI}
                    disabled={aiGenerating || !plan.name.trim()}
                  >
                    {aiGenerating ? 'Generating...' : 'AI Add Meals'}
                  </Button>
                )}
                <Button size="small" startIcon={<Add />} onClick={addDay}>Add Day</Button>
              </Stack>
            </Stack>
            {canUseAI && (
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                AI uses Basic Info and Daily Nutrition Targets to generate dietician-style days, meals, food items, and macros.
              </Typography>
            )}
            {plan.days.map((day, di) => (
              <MealDayEditor key={di} day={day} dayIndex={di} foodItems={foodItems}
                onUpdateDay={updateDay} onAddMeal={addMeal} onUpdateMeal={updateMeal}
                onRemoveMeal={removeMeal} onAddFood={addFoodToMeal} onRemoveFood={removeFoodFromMeal}
                onRemoveDay={removeDay} canRemove={plan.days.length > 1} />
            ))}
          </Box>
        )}

      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={aiGenerating}>Cancel</Button>
        {tab > 0 && <Button onClick={() => setTab(t => Math.max(t - 1, 0))} disabled={saving || aiGenerating}>Back</Button>}
        <Button variant="contained" onClick={tab < 2 ? handleNext : handleSave} disabled={saving || aiGenerating}>
          {tab < 2 ? 'Next' : saving ? 'Saving...' : plan._id ? 'Update Plan' : 'Create Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Meal Day Editor ────────────────────────────────────────────────────────────
function MealDayEditor({ day, dayIndex, foodItems, onUpdateDay, onAddMeal, onUpdateMeal, onRemoveMeal, onAddFood, onRemoveFood, onRemoveDay, canRemove }) {
  const [expanded, setExpanded] = useState(dayIndex === 0);
  const dayTotal = (day.meals || []).reduce((s, m) => s + (m.totalCalories || 0), 0);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
          {day.dayName}
          <Chip label={`${(day.meals || []).length} meals`} size="small" color="primary" sx={{ ml: 1 }} />
          {dayTotal > 0 && <Chip label={`${dayTotal.toFixed(0)} kcal`} size="small" sx={{ ml: 1 }} />}
        </Typography>
        {canRemove && <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onRemoveDay(dayIndex); }}><Delete fontSize="small" /></IconButton>}
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </Box>
      <Collapse in={expanded}>
        <Divider />
        <Box p={2}>
          <TextField label="Day Name" value={day.dayName} onChange={e => onUpdateDay(dayIndex, 'dayName', e.target.value)} size="small" sx={{ mb: 2, width: 200 }} />
          {(day.meals || []).map((meal, mi) => (
            <MealEditor key={mi} meal={meal} mealIndex={mi} dayIndex={dayIndex} foodItems={foodItems}
              onUpdate={onUpdateMeal} onRemove={onRemoveMeal} onAddFood={onAddFood} onRemoveFood={onRemoveFood} />
          ))}
          <Button size="small" startIcon={<Add />} onClick={() => onAddMeal(dayIndex)} sx={{ mt: 1 }}>Add Meal</Button>
        </Box>
      </Collapse>
    </Card>
  );
}

// ── Meal Editor ────────────────────────────────────────────────────────────────
function MealEditor({ meal, mealIndex, dayIndex, foodItems, onUpdate, onRemove, onAddFood, onRemoveFood }) {
  const [selectedFood, setSelectedFood] = useState(null);
  const [weight, setWeight] = useState('');

  const handleAddFood = () => {
    if (!selectedFood) return;
    onAddFood(dayIndex, mealIndex, selectedFood, weight || selectedFood.defaultServing || 100);
    setSelectedFood(null);
    setWeight('');
  };

  return (
    <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <TextField select label="Meal Type" value={meal.mealType} onChange={e => onUpdate(dayIndex, mealIndex, 'mealType', e.target.value)} size="small" sx={{ width: 180 }}>
          {MEAL_TYPES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
        <TextField label="Time" type="time" value={meal.time} onChange={e => onUpdate(dayIndex, mealIndex, 'time', e.target.value)} size="small" sx={{ width: 120 }} InputLabelProps={{ shrink: true }} />
        <Box sx={{ flexGrow: 1 }} />
        {meal.totalCalories > 0 && <Chip label={`${meal.totalCalories.toFixed(0)} kcal`} size="small" color="warning" />}
        <IconButton size="small" color="error" onClick={() => onRemove(dayIndex, mealIndex)}><Delete fontSize="small" /></IconButton>
      </Stack>

      {(meal.items || []).map((item, ii) => (
        <Stack key={ii} direction="row" alignItems="center" spacing={1} mb={0.5} sx={{ 
          bgcolor: item.isRecommended ? 'success.50' : 'action.hover', 
          p: 1, 
          borderRadius: 1,
          border: item.isRecommended ? 1 : 0,
          borderColor: item.isRecommended ? 'success.main' : 'transparent',
        }}>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {item.name} — {item.weightG}g
            {item.isRecommended && <Chip label="Recommended" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
          </Typography>
          <Typography variant="caption" color="text.secondary">{item.calories.toFixed(0)} kcal · P {item.proteinG.toFixed(1)}g · C {item.carbsG.toFixed(1)}g · F {item.fatG.toFixed(1)}g</Typography>
          <IconButton size="small" onClick={() => onRemoveFood(dayIndex, mealIndex, ii)}><Close fontSize="small" /></IconButton>
        </Stack>
      ))}

      <Stack direction="row" spacing={1} mt={1} alignItems="center">
        <Autocomplete
          options={foodItems}
          getOptionLabel={o => o.name}
          value={selectedFood}
          onChange={(_, v) => { setSelectedFood(v); if (v) setWeight(String(v.defaultServing || 100)); }}
          renderInput={params => <TextField {...params} label="Search food" size="small" InputProps={{ ...params.InputProps, startAdornment: <><Search fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} />{params.InputProps.startAdornment}</> }} />}
          sx={{ flexGrow: 1 }}
          size="small"
        />
        <TextField label="g" type="number" value={weight} onChange={e => setWeight(e.target.value)} size="small" sx={{ width: 80 }} />
        <Button size="small" variant="outlined" onClick={handleAddFood} disabled={!selectedFood}>Add</Button>
      </Stack>
    </Box>
  );
}

// ── Weekly Schedule Dialog ─────────────────────────────────────────────────────
// Trainer creates a weekly food schedule for a strict meal plan.
// Each schedule covers Mon–Sun with per-day, per-meal-type food items.
function WeeklyScheduleDialog({ plan, onClose }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await weeklySchedulesAPI.getForPlan(plan._id);
      setSchedules(res.data.data || []);
    } catch (e) {
      setError('Failed to load schedules');
    } finally { setLoading(false); }
  }, [plan._id]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const handleSaveSchedule = async (schedule) => {
    try {
      if (schedule._id) {
        await weeklySchedulesAPI.update(schedule._id, schedule);
        setSuccess('Schedule updated');
      } else {
        await weeklySchedulesAPI.create({ ...schedule, mealPlanId: plan._id });
        setSuccess('Schedule created');
      }
      setCreatorOpen(false);
      setEditingSchedule(null);
      fetchSchedules();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save schedule');
    }
  };

  const handleDelete = async () => {
    try {
      await weeklySchedulesAPI.delete(deleteId);
      setSuccess('Schedule deleted');
      setDeleteId(null);
      fetchSchedules();
    } catch (e) { setError('Failed to delete'); }
  };

  const emptySchedule = () => ({
    weekNumber: (schedules.length > 0 ? Math.max(...schedules.map(s => s.weekNumber || 0)) + 1 : 1),
    weekLabel: '',
    days: DAY_NAMES.map((name, i) => ({ dayOfWeek: i + 1, dayName: name, meals: [] })),
  });

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Weekly Schedules</Typography>
            <Typography variant="caption" color="text.secondary">{plan.name} — Strict Mode</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" startIcon={<Add />} onClick={() => { setEditingSchedule(emptySchedule()); setCreatorOpen(true); }}>
              New Week
            </Button>
            <IconButton onClick={onClose}><Close /></IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : schedules.length === 0 ? (
          <Box textAlign="center" py={6}>
            <CalendarMonth sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No weekly schedules yet.</Typography>
            <Typography variant="caption" color="text.secondary">Create a schedule to define what users eat each day this week.</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {schedules.map(sch => (
              <Card key={sch._id} variant="outlined">
                <CardContent sx={{ pb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography fontWeight={600}>{sch.weekLabel || `Week ${sch.weekNumber}`}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sch.days?.length || 0} days · {sch.days?.reduce((s, d) => s + (d.meals?.length || 0), 0) || 0} meals configured
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<Edit />} onClick={() => { setEditingSchedule(sch); setCreatorOpen(true); }}>Edit</Button>
                      <IconButton size="small" color="error" onClick={() => setDeleteId(sch._id)}><Delete fontSize="small" /></IconButton>
                    </Stack>
                  </Stack>
                  {/* Day summary chips */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={1} gap={0.5}>
                    {(sch.days || []).map(d => (
                      <Chip
                        key={d.dayOfWeek}
                        label={`${d.dayName.slice(0,3)} (${d.meals?.length || 0})`}
                        size="small"
                        color={d.meals?.length > 0 ? 'primary' : 'default'}
                        variant={d.meals?.length > 0 ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>

      {creatorOpen && editingSchedule && (
        <ScheduleCreatorDialog
          schedule={editingSchedule}
          planId={plan._id}
          onSave={handleSaveSchedule}
          onClose={() => { setCreatorOpen(false); setEditingSchedule(null); }}
        />
      )}

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Schedule?</DialogTitle>
        <DialogContent><Typography>This will remove the weekly food schedule.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

// ── Schedule Creator Dialog ────────────────────────────────────────────────────
function ScheduleCreatorDialog({ schedule: initialSchedule, planId, onSave, onClose }) {
  const { foodItems: cachedFoodItems, loadFoodItems } = useAppData();
  const [schedule, setSchedule] = useState(() => ({
    ...initialSchedule,
    days: (initialSchedule.days || DAY_NAMES.map((name, i) => ({ dayOfWeek: i + 1, dayName: name, meals: [] }))),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState(0); // index into days array

  // Trigger lazy-load of food items (no-op if already cached)
  useEffect(() => { loadFoodItems(); }, [loadFoodItems]);

  // Use cached food items from context
  const foodItems = cachedFoodItems;

  const updateField = (field, value) => setSchedule(s => ({ ...s, [field]: value }));

  const addMealToDay = (dayIdx) => {
    const days = [...schedule.days];
    days[dayIdx] = {
      ...days[dayIdx],
      meals: [...(days[dayIdx].meals || []), { mealType: 'lunch', items: [] }],
    };
    setSchedule(s => ({ ...s, days }));
  };

  const removeMealFromDay = (dayIdx, mealIdx) => {
    const days = [...schedule.days];
    days[dayIdx] = { ...days[dayIdx], meals: days[dayIdx].meals.filter((_, i) => i !== mealIdx) };
    setSchedule(s => ({ ...s, days }));
  };

  const updateMealType = (dayIdx, mealIdx, mealType) => {
    const days = [...schedule.days];
    days[dayIdx].meals[mealIdx] = { ...days[dayIdx].meals[mealIdx], mealType };
    setSchedule(s => ({ ...s, days }));
  };

  const addFoodToScheduledMeal = (dayIdx, mealIdx, food, quantityUnit, quantityValue) => {
    const qv = parseFloat(quantityValue) || 1;
    const approxG = effectiveGrams(quantityUnit, qv, food.defaultServing || 100);
    const factor = approxG / 100;
    const item = {
      foodItemId: food._id,
      name: food.name,
      quantityUnit,
      quantityValue: qv,
      effectiveWeightG: approxG,
      caloriesPerUnit: +(food.cal100 * (quantityUnit === 'grams' ? 1 / 100 : (quantityUnit === 'count' ? (food.defaultServing || 100) / 100 : (quantityUnit === 'small_bowl' ? 2 : 4)))).toFixed(1),
      proteinPerUnit:  +(food.protein100 * factor / qv).toFixed(1),
      carbsPerUnit:    +(food.carbs100 * factor / qv).toFixed(1),
      fatPerUnit:      +(food.fat100 * factor / qv).toFixed(1),
      fiberPerUnit:    +((food.fiber100 || 0) * factor / qv).toFixed(1),
    };
    const days = [...schedule.days];
    days[dayIdx].meals[mealIdx] = {
      ...days[dayIdx].meals[mealIdx],
      items: [...(days[dayIdx].meals[mealIdx].items || []), item],
    };
    setSchedule(s => ({ ...s, days }));
  };

  const removeFoodFromScheduledMeal = (dayIdx, mealIdx, itemIdx) => {
    const days = [...schedule.days];
    days[dayIdx].meals[mealIdx] = {
      ...days[dayIdx].meals[mealIdx],
      items: days[dayIdx].meals[mealIdx].items.filter((_, i) => i !== itemIdx),
    };
    setSchedule(s => ({ ...s, days }));
  };

  const handleSave = async () => {
    if (!schedule.weekNumber || schedule.weekNumber < 1) {
      setError('Week number is required');
      return;
    }
    setSaving(true);
    try { await onSave(schedule); }
    catch (e) { setError(e.message || 'Save failed'); setSaving(false); }
  };

  const currentDay = schedule.days[selectedDay];

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{schedule._id ? 'Edit' : 'Create'} Weekly Schedule</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Week number + label */}
        <Grid container spacing={2} mb={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField
              label="Week Number *"
              type="number"
              value={schedule.weekNumber || ''}
              onChange={e => {
                const n = parseInt(e.target.value, 10);
                updateField('weekNumber', n);
                // Auto-fill label if not customised
                if (!schedule._weekLabelCustomised) {
                  updateField('weekLabel', `Week ${n}`);
                }
              }}
              fullWidth
              inputProps={{ min: 1 }}
              helperText="e.g. 1, 2, 3…"
            />
          </Grid>
          <Grid item xs={6} sm={9}>
            <TextField
              label="Week Label"
              placeholder={`Week ${schedule.weekNumber || 1}`}
              value={schedule.weekLabel || ''}
              onChange={e => {
                updateField('weekLabel', e.target.value);
                updateField('_weekLabelCustomised', true);
              }}
              fullWidth
              helperText="Optional custom label shown to users"
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" fontWeight={600} mb={1}>Select Day</Typography>
        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={0.5}>
          {schedule.days.map((d, i) => (
            <Chip
              key={i}
              label={`${d.dayName.slice(0, 3)} (${d.meals?.length || 0})`}
              onClick={() => setSelectedDay(i)}
              color={selectedDay === i ? 'primary' : (d.meals?.length > 0 ? 'success' : 'default')}
              variant={selectedDay === i ? 'filled' : 'outlined'}
              clickable
            />
          ))}
        </Stack>

        {/* Day meal editor */}
        <Card variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight={600}>{currentDay?.dayName}</Typography>
            <Button size="small" startIcon={<Add />} onClick={() => addMealToDay(selectedDay)}>Add Meal Type</Button>
          </Stack>

          {(!currentDay?.meals || currentDay.meals.length === 0) && (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              No meals configured for this day. Click "Add Meal Type" to start.
            </Typography>
          )}

          {(currentDay?.meals || []).map((meal, mi) => (
            <ScheduledMealEditor
              key={mi}
              meal={meal}
              mealIndex={mi}
              dayIndex={selectedDay}
              foodItems={foodItems}
              onUpdateMealType={updateMealType}
              onRemoveMeal={removeMealFromDay}
              onAddFood={addFoodToScheduledMeal}
              onRemoveFood={removeFoodFromScheduledMeal}
            />
          ))}
        </Card>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : schedule._id ? 'Update Schedule' : 'Create Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Scheduled Meal Editor ──────────────────────────────────────────────────────
function ScheduledMealEditor({ meal, mealIndex, dayIndex, foodItems, onUpdateMealType, onRemoveMeal, onAddFood, onRemoveFood }) {
  const [selectedFood, setSelectedFood] = useState(null);
  const [quantityUnit, setQuantityUnit] = useState('grams');
  const [quantityValue, setQuantityValue] = useState('100');
  const [customMode, setCustomMode] = useState(false);

  // Custom food fields
  const [customName, setCustomName]       = useState('');
  const [customCal, setCustomCal]         = useState('');
  const [customProtein, setCustomProtein] = useState('0');
  const [customCarbs, setCustomCarbs]     = useState('0');
  const [customFat, setCustomFat]         = useState('0');

  const handleAdd = () => {
    if (customMode) {
      if (!customName.trim() || !customCal) return;
      const qv = parseFloat(quantityValue) || 1;
      const approxG = effectiveGrams(quantityUnit, qv, 100);
      const food = {
        _id: null,
        name: customName.trim(),
        cal100: quantityUnit === 'grams'
          ? (parseFloat(customCal) / qv) * 100
          : parseFloat(customCal),
        protein100: quantityUnit === 'grams'
          ? (parseFloat(customProtein || 0) / qv) * 100
          : parseFloat(customProtein || 0),
        carbs100: quantityUnit === 'grams'
          ? (parseFloat(customCarbs || 0) / qv) * 100
          : parseFloat(customCarbs || 0),
        fat100: quantityUnit === 'grams'
          ? (parseFloat(customFat || 0) / qv) * 100
          : parseFloat(customFat || 0),
        fiber100: 0,
        defaultServing: approxG,
      };
      onAddFood(dayIndex, mealIndex, food, quantityUnit, quantityValue);
      setCustomName(''); setCustomCal(''); setCustomProtein('0'); setCustomCarbs('0'); setCustomFat('0');
    } else {
      if (!selectedFood) return;
      onAddFood(dayIndex, mealIndex, selectedFood, quantityUnit, quantityValue);
      setSelectedFood(null);
      setQuantityValue(quantityUnit === 'grams' ? '100' : '1');
    }
  };

  const quantityLabel = (item) => {
    if (item.quantityUnit === 'grams') return `${item.quantityValue}g`;
    if (item.quantityUnit === 'small_bowl') return `${item.quantityValue} small bowl${item.quantityValue > 1 ? 's' : ''} (~${item.effectiveWeightG}g)`;
    if (item.quantityUnit === 'large_bowl') return `${item.quantityValue} large bowl${item.quantityValue > 1 ? 's' : ''} (~${item.effectiveWeightG}g)`;
    if (item.quantityUnit === 'count') return `${item.quantityValue} piece${item.quantityValue > 1 ? 's' : ''} (~${item.effectiveWeightG}g)`;
    return `${item.quantityValue}`;
  };

  const canAdd = customMode
    ? (customName.trim() && customCal)
    : !!selectedFood;

  return (
    <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      {/* Meal type header */}
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <TextField
          select label="Meal Type" value={meal.mealType}
          onChange={e => onUpdateMealType(dayIndex, mealIndex, e.target.value)}
          size="small" sx={{ width: 200 }}
        >
          {MEAL_TYPES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Chip label={`${(meal.items || []).length} items`} size="small" color="primary" />
        <IconButton size="small" color="error" onClick={() => onRemoveMeal(dayIndex, mealIndex)}><Delete fontSize="small" /></IconButton>
      </Stack>

      {/* Prescribed food items list */}
      {(meal.items || []).map((item, ii) => (
        <Stack key={ii} direction="row" alignItems="center" spacing={1} mb={0.5} sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" fontWeight={500}>{item.name}</Typography>
            <Typography variant="caption" color="text.secondary">{quantityLabel(item)}</Typography>
          </Box>
          <IconButton size="small" onClick={() => onRemoveFood(dayIndex, mealIndex, ii)}><Close fontSize="small" /></IconButton>
        </Stack>
      ))}

      {/* Add food section */}
      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
        {/* Toggle: search vs custom */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">Add Food Item</Typography>
          <Button
            size="small"
            variant={customMode ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => { setCustomMode(m => !m); setSelectedFood(null); }}
            sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
          >
            {customMode ? '← Search List' : '+ Custom Food'}
          </Button>
        </Stack>

        {!customMode ? (
          /* ── Search from food database ── */
          <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" gap={1}>
            <Autocomplete
              options={foodItems}
              getOptionLabel={o => o.name}
              value={selectedFood}
              onChange={(_, v) => {
                setSelectedFood(v);
                setQuantityValue(quantityUnit === 'grams' ? String(v?.defaultServing || 100) : '1');
              }}
              renderInput={params => (
                <TextField
                  {...params} label="Search food" size="small"
                  InputProps={{ ...params.InputProps, startAdornment: <><Search fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} />{params.InputProps.startAdornment}</> }}
                />
              )}
              sx={{ minWidth: 200, flexGrow: 1 }}
              size="small"
            />
            <TextField
              select label="Unit" value={quantityUnit}
              onChange={e => {
                setQuantityUnit(e.target.value);
                setQuantityValue(e.target.value === 'grams' ? String(selectedFood?.defaultServing || 100) : '1');
              }}
              size="small" sx={{ width: 180 }}
            >
              {QUANTITY_UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
            </TextField>
            <TextField
              label={quantityUnit === 'grams' ? 'Amount (g)' : quantityUnit === 'count' ? 'Count' : 'Bowls'}
              type="number" value={quantityValue}
              onChange={e => setQuantityValue(e.target.value)}
              size="small" sx={{ width: 100 }}
              inputProps={{ min: 0.1, step: quantityUnit === 'grams' ? 10 : 0.5 }}
            />
            <Button size="small" variant="contained" onClick={handleAdd} disabled={!canAdd} sx={{ height: 40 }}>Add</Button>
          </Stack>
        ) : (
          /* ── Custom food entry ── */
          <Box>
            <Grid container spacing={1} mb={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Food name *" value={customName}
                  onChange={e => setCustomName(e.target.value)}
                  size="small" fullWidth placeholder="e.g. Homemade Dal, Protein Bar"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  select label="Unit" value={quantityUnit}
                  onChange={e => { setQuantityUnit(e.target.value); setQuantityValue(e.target.value === 'grams' ? '100' : '1'); }}
                  size="small" fullWidth
                >
                  {QUANTITY_UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label={quantityUnit === 'grams' ? 'Amount (g)' : quantityUnit === 'count' ? 'Count' : 'Bowls'}
                  type="number" value={quantityValue}
                  onChange={e => setQuantityValue(e.target.value)}
                  size="small" fullWidth
                  inputProps={{ min: 0.1, step: quantityUnit === 'grams' ? 10 : 0.5 }}
                />
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Nutrition for the quantity above:
            </Typography>
            <Grid container spacing={1} mb={1}>
              <Grid item xs={6} sm={3}>
                <TextField label="Calories (kcal) *" type="number" value={customCal} onChange={e => setCustomCal(e.target.value)} size="small" fullWidth inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField label="Protein (g)" type="number" value={customProtein} onChange={e => setCustomProtein(e.target.value)} size="small" fullWidth inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField label="Carbs (g)" type="number" value={customCarbs} onChange={e => setCustomCarbs(e.target.value)} size="small" fullWidth inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField label="Fat (g)" type="number" value={customFat} onChange={e => setCustomFat(e.target.value)} size="small" fullWidth inputProps={{ min: 0 }} />
              </Grid>
            </Grid>
            <Button size="small" variant="contained" onClick={handleAdd} disabled={!canAdd}>Add Custom Food</Button>
          </Box>
        )}

        {/* Effective weight hint */}
        {!customMode && selectedFood && quantityUnit !== 'grams' && (
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
            ≈ {effectiveGrams(quantityUnit, parseFloat(quantityValue) || 1, selectedFood.defaultServing || 100).toFixed(0)}g effective weight
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
// (effectiveGrams is defined at the top of the file)

// ── Import Excel Dialog ────────────────────────────────────────────────────────
function ImportExcelDialog({ onClose, onImported }) {
  const [planName, setPlanName]     = useState('');
  const [dietType, setDietType]     = useState('vegetarian');
  const [goal, setGoal]             = useState('healthy_eating');
  const [description, setDescription] = useState('');
  const [isFree, setIsFree]         = useState(true);
  const [file, setFile]             = useState(null);
  const [importing, setImporting]   = useState(false);
  const [error, setError]           = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await mealPlansAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href    = url;
      a.download = 'meal_plan_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleImport = async () => {
    if (!planName.trim()) { setError('Plan name is required'); return; }
    if (!file)            { setError('Please select an Excel file'); return; }

    setImporting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('planName', planName.trim());
      fd.append('dietType', dietType);
      fd.append('goal', goal);
      fd.append('description', description.trim());
      fd.append('isFree', String(isFree));
      await mealPlansAPI.importExcel(fd);
      onImported();
    } catch (e) {
      setError(e.response?.data?.message || 'Import failed');
      setImporting(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Import Meal Plan from Excel</Typography>
            <Typography variant="caption" color="text.secondary">
              Upload an Excel file to auto-create food items and a meal plan
            </Typography>
          </Box>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Download template */}
        <Box
          sx={{
            p: 2, mb: 3, border: 1, borderColor: 'primary.light',
            borderRadius: 2, bgcolor: 'primary.50', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>Download Sample Template</Typography>
            <Typography variant="caption" color="text.secondary">
              Use this template to format your meal plan data correctly
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={14} /> : <Download />}
            onClick={handleDownloadTemplate}
            disabled={downloading}
          >
            Template
          </Button>
        </Box>

        <Grid container spacing={2}>
          {/* Plan name */}
          <Grid item xs={12}>
            <TextField
              label="Plan Name *"
              value={planName}
              onChange={e => setPlanName(e.target.value)}
              fullWidth
              placeholder="e.g. 30-Day Veg Diet Plan"
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description for this plan"
            />
          </Grid>

          {/* Diet type — veg / non-veg / vegan */}
          <Grid item xs={6}>
            <TextField
              select
              label="Diet Type"
              value={dietType}
              onChange={e => setDietType(e.target.value)}
              fullWidth
            >
              <MenuItem value="vegetarian">🥦 Vegetarian</MenuItem>
              <MenuItem value="vegan">🌱 Vegan</MenuItem>
              <MenuItem value="regular">🍗 Non-Vegetarian</MenuItem>
              <MenuItem value="keto">🥑 Keto</MenuItem>
              <MenuItem value="paleo">🥩 Paleo</MenuItem>
              <MenuItem value="mediterranean">🫒 Mediterranean</MenuItem>
            </TextField>
          </Grid>

          {/* Goal */}
          <Grid item xs={6}>
            <TextField
              select
              label="Goal"
              value={goal}
              onChange={e => setGoal(e.target.value)}
              fullWidth
            >
              <MenuItem value="healthy_eating">Healthy Eating</MenuItem>
              <MenuItem value="weight_loss">Weight Loss</MenuItem>
              <MenuItem value="muscle_gain">Muscle Gain</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </TextField>
          </Grid>

          {/* Free toggle */}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={isFree} onChange={e => setIsFree(e.target.checked)} />}
              label="Free Plan"
            />
          </Grid>

          {/* File upload */}
          <Grid item xs={12}>
            <Box
              sx={{
                border: 2,
                borderStyle: 'dashed',
                borderColor: file ? 'success.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: file ? 'success.50' : 'grey.50',
                transition: 'all 0.15s',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
              }}
              onClick={() => document.getElementById('excel-upload').click()}
            >
              <input
                id="excel-upload"
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={e => setFile(e.target.files[0] || null)}
              />
              <Upload sx={{ fontSize: 36, color: file ? 'success.main' : 'text.disabled', mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>
                {file ? file.name : 'Click to select Excel file'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB — ready to import`
                  : 'Supports .xlsx and .xls files (max 5 MB)'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {importing && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
              Parsing Excel, creating food items and meal plan…
            </Typography>
            <LinearProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={importing}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <Upload />}
          onClick={handleImport}
          disabled={importing || !file || !planName.trim()}
        >
          {importing ? 'Importing…' : 'Import Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
